import { FC, useMemo, useState } from "react"
import { useContextSelector as useCS } from "use-context-selector"
import { LoadingIcon16, EmptyScreen } from "@unlimint/admin-ui-kit"
import { useNavigate } from "react-router-dom"

import { FormContainer } from "domain/core/containers/FormContainer"
import { FormModeEnum } from "domain/enums/FormModeEnum"
import { CustomizationContext } from "domain/customization/CustomizationContext"
import { customizationSelectors } from "domain/customization/selectors"
import {
  TCustomizationListItemRes,
  ThemeEnum,
} from "domain/customization/types"
import { useOpenable } from "domain/hooks/useOpenable"
import { validate } from "domain/customization/containers/EditThemesContainer/validate"

import { TCustomisactionThemePageProps } from "./index"
import * as S from "./styled"

type TCustomizationThemePageContainerProps = {
  Component: (props: TCustomisactionThemePageProps) => JSX.Element
}

export const CustomizationThemePageContainer: FC<
  TCustomizationThemePageContainerProps
> = ({ Component }) => {
  const item = useCS(CustomizationContext, customizationSelectors.item)
  const update = useCS(CustomizationContext, customizationSelectors.update)

  const [isBackFlow, setIsBackFlow] = useState(false)

  const navigate = useNavigate()

  const saveOpenable = useOpenable()

  const initialValues = useMemo(() => {
    if (!item.data) return {}
    return {
      name: item.data.name,
      [ThemeEnum.Light]: item.data[ThemeEnum.Light],
      [ThemeEnum.Dark]: item.data[ThemeEnum.Dark],
    }
  }, [item.data])

  const onBack = () => {
    navigate("/dev-space/customization")
  }

  const handleSetIsBackFlow = (v: boolean) => {
    setIsBackFlow(v)
  }

  const handleSuccess = (data: {
    styles: Array<TCustomizationListItemRes>
  }) => {
    const newData = data?.styles.find(
      (config) => config.name === item.data.name
    )

    if (!newData) return

    update({
      item: {
        ...item,
        data: {
          ...item.data,
          ...newData,
        },
      },
    })

    if (isBackFlow) onBack()
  }

  if (item.error) {
    return (
      <S.EmptyScreenWrapp>
        <EmptyScreen emptyText="There aren't any results for that customization name" />
      </S.EmptyScreenWrapp>
    )
  }

  if (!item.data) {
    return (
      <S.LoadingWrapp>
        <LoadingIcon16 />
      </S.LoadingWrapp>
    )
  }

  return (
    <FormContainer
      mode={FormModeEnum.Edit}
      url="/v1/merchants/styles"
      initialValues={initialValues}
      validate={validate}
      mapToReqModel={(data) => ({
        styles: [data],
      })}
      Form={Component}
      formProps={{
        data: item.data,
        error: item.error,
        onClose: saveOpenable.onClose,
        saveOpenable,
        onBack,
        setIsBackFlow: handleSetIsBackFlow,
      }}
      withCommonError={false}
      cbSuccess={handleSuccess}
    />
  )
}
